import { Component, CUSTOM_ELEMENTS_SCHEMA, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { Properties } from '@loup-transload-ui-app/common-interfaces';
import { PropertyLookupService } from '@loup-transload-ui-app/common-services';


type ErrorState = 'warning' | 'error';

@Component({
  selector: 'lib-external-dropdown',
  imports: [CommonModule, ReactiveFormsModule],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  templateUrl: './external-dropdown.component.html',
  styleUrl: './external-dropdown.component.scss'
})

export class ExternalDropdownComponent implements OnInit, OnChanges, OnDestroy {
  @Input() appearance = "fill" // posible values could be "fill" or "standard" or  "outline" or "legacy"
   @Input() propertyCodes: any;
   @Input() propertyType = 'PUBLIC_PROGRAM_CATEGORY';
   @Input() dropDownName: string;
   @Input() isSubmitted: boolean;
   @Input() type = 'full';
   @Input() labelName = 'Public program category';
   @Input() isGlobalSearch = true;
   @Input() fullWidth = false;
   @Input() customCss = null;
   @Input() required = false;
   @Input() showErrors = true;
   @Output() selectedType = new EventEmitter<any>();
   @Input() formControlData: any;
   @Input() disabled = false;
   @Input() pageName = '';
   @Input() moveTypeName= '';
   @Input() commodityCategory= '';
   @Input() showGetData = false;
   dropdownForm: FormControl;
   public selectedValues: any = [];
   public selectedPalletValues: any = [];
   public selectedCarValues: any = [];
   propertyTypeValues: Properties[];
   private destroy$ = new Subject<void>();
   translations: any = {};
   complexData: any = [];
   backUpComplexData: any = [];
   customTemplate = (item: any) => `<span>${item.displayText}</span>`;
   formatForInput = (item: any) => (item ? item.displayText : '');
   message = '';
   filterKey = 'displayText';
   dropdownState: ErrorState;
   isValueAvailable = false;
   dropdownValue: any;
 
   constructor(private propertyLookupService: PropertyLookupService) {
     this.dropdownForm = new FormControl('');
   }
 
   ngOnInit(): void {
     this.selectDropdownData();
   }
 
   selectDropdownData() {
     this.loadPropertyTypes();
     this.dropdownForm = new FormControl('', { validators: [Validators.required] });
     this.dropdownForm.statusChanges.subscribe(() => this.updateStatus());
   }
 
   updateStatus() {
     if (this.showErrors) {
       const found = this.propertyTypeValues.find((element) => element.displayText === this.dropdownForm.value);
       if (this.dropdownForm.valid && found) {
         this.message = null;
         this.dropdownState = null;
       } else {
         if(this.dropdownForm.touched){
           this.showErrorWarning();
         }
       }
     }
   }
 
   showErrorWarning() {
     if (!this.dropdownForm.value && !this.message) {
       this.message = this.labelName + ' is required.';
     }
     if(this.message){
       this.dropdownState = 'warning';
       if (this.isSubmitted) {
         this.dropdownState = 'error';
       }
     }
   }
 
   ngOnChanges(changes: SimpleChanges): void {
     if (changes['isSubmitted']?.currentValue === true) {
       this.showErrorWarning();
     }
     const { moveTypeName, commodityCategory } = changes;
     if ((moveTypeName || commodityCategory) && this.dropDownName === 'PUBLIC_PROGRAM_NAME' && !this.disabled) {
       this.resetDropdown();
       const categoryDescription = this.formControlData.value.commodityCategory?.description;
       const formattedMoveTypeName = this.formatMoveTypeName(this.moveTypeName);
       this.filterComplexData(categoryDescription, formattedMoveTypeName);
     }
   }
   
   private resetDropdown(): void {
     if (this.dropdownForm?.value) {
       this.dropdownForm.setValue(null);
       this.selectedType.emit(null);
     }
   }
   
   private formatMoveTypeName(moveTypeName: string): string {
     return moveTypeName?.charAt(0).toUpperCase() + moveTypeName?.slice(1).toLowerCase();
   }
   
   private filterComplexData(categoryDescription: string | undefined, formattedMoveTypeName: string): void {
     this.complexData = this.backUpComplexData.filter(ele => 
       (!categoryDescription || ele.category === categoryDescription) &&
       ele.subCategory.includes(formattedMoveTypeName)
     );
   }
   
   resetData() {
     this.selectDropdownData();
   }
 

   loadPropertyTypes() {
    if(!this.showGetData){
     this.propertyLookupService.getCarloadKeyValueExternal(this.propertyType).pipe(takeUntil(this.destroy$)).subscribe((result: any) => {
         this.propertyTypeValues = result;
         this.backUpComplexData = [...this.propertyTypeValues];
         this.complexData = this.propertyTypeValues;
         if(this.dropDownName === 'PUBLIC_PROGRAM_NAME' ){
          setTimeout(() => {
           this.forPublicprogram();
           this.setDropdownValue();
          }, 300);
         }else{
           this.setDropdownValue();
         }
       });
      }else{
         this.propertyLookupService.getDocumentType()
          .pipe(takeUntil(this.destroy$))
          .subscribe(result => {
            if (result?.errors?.length) {
              return;
            }
            this.propertyTypeValues = result.data;
            this.complexData = [...result.data];
          });
      }
   }
 
   forPublicprogram(){
     if(this.formControlData.value.moveType && this.dropDownName === 'PUBLIC_PROGRAM_NAME' && this.backUpComplexData && this.backUpComplexData.length > 0){
       if (this.formControlData.value.commodityCategory ) {
         this.complexData = this.backUpComplexData.filter(ele => ele.category == this.formControlData.value.commodityCategory.description && ele.subCategory.includes(this.formControlData.value.moveType.charAt(0).toUpperCase() + this.formControlData.value.moveType.slice(1).toLowerCase()))
       } else {
         this.complexData = this.backUpComplexData.filter(ele => ele.subCategory.includes(this.formControlData.value.moveType.charAt(0).toUpperCase() + this.formControlData.value.moveType.slice(1).toLowerCase()));
       }
     }
   }
 
   setDropdownValue() {
       let foundIndex = null;
       switch (this.propertyType) {
         case 'PUBLIC_PROGRAM_CATEGORY':
           if (this.formControlData && this.formControlData.value.commodityCategory && this.formControlData.value.commodityCategory.code) {
             foundIndex = this.propertyTypeValues.findIndex((element) => element.code === this.formControlData.value.commodityCategory.code);
           }
         break;
         case 'PUBLIC_PROGRAM_NAME':
           if (this.formControlData && this.formControlData.value.programName && this.formControlData.value.programName.code) {
             foundIndex = this.propertyTypeValues.findIndex((element) => element.code === this.formControlData.value.programName.code);
           }
         break;
         case 'CARLOAD_ACCESSORIAL':
          if (this.formControlData && this.formControlData.value.service && this.formControlData.value.service.code) {
            foundIndex = this.propertyTypeValues.findIndex((element) => element.code === this.formControlData.value.service.code);
          }
        break;
        case 'VOLUME_DURATION_UOM':
          if (this.formControlData && this.formControlData.value.timeFrame && this.formControlData.value.timeFrame.code) {
            foundIndex = this.propertyTypeValues.findIndex((element) => element.code === this.formControlData.value.timeFrame.code);
          }
        break;
        case 'VOLUME_UOM':
          if (this.formControlData && this.formControlData.value.unitOfMeasure && this.formControlData.value.unitOfMeasure.code) {
            foundIndex = this.propertyTypeValues.findIndex((element) => element.code === this.formControlData.value.unitOfMeasure.code);
          }
        break;
         default:
         break;
       }
       this.setValueToDropDown(foundIndex);
       if (Boolean(this.selectedValues) && this.propertyType === this.selectedValues.type) {
        this.updateForm();
       }
       if (this.formControlData && this.formControlData.value.volumeUOM && this.formControlData.value.volumeUOM.id) {
         foundIndex = this.propertyTypeValues.findIndex((element) => element.id === this.formControlData.value.volumeUOM.id);
       };
   }
 
   setValueToDropDown(foundIndex){
     const selectedGroups = [];
     if (foundIndex !== null && foundIndex !== -1) {
       this.selectedValues = this.propertyTypeValues[foundIndex];
       this.updateForm();
       selectedGroups.push(this.selectedValues);
       this.selectedType.emit(selectedGroups);
     } else {
       this.makeDefault(); /** If nothing is available in the dropdown we are selecting 1st element */
       if(this.selectedValues){
         this.updateForm();
         selectedGroups.push(this.selectedValues);
         this.selectedType.emit(selectedGroups);
       }
     } 
   }
 
   makeDefault(){
     if(this.dropDownName === 'freeTimeUOM'){
       this.selectedValues = this.propertyTypeValues[1];
     }
     if(this.dropDownName === 'volumeDuration'){
       this.selectedValues = this.propertyTypeValues[2];
     }
     if(this.dropDownName === 'volumeDurationUom'){
       this.selectedValues = this.propertyTypeValues[0];
     }  
   }
 
   updateForm() {
     this.dropdownForm.setValue(this.selectedValues?.displayText);
   }
 
   onAppointmentTypeSelect(event: Event) {
     const { detail }: CustomEvent = event as CustomEvent;
     if (!detail) {
       this.selectedType.emit(null);
       return;
     }
     this.dropdownState = null; 
     if (this.type === 'full') {
         this.dropdownForm.setValue(detail.displayText);
         this.selectedType.emit([detail]);
     } else {
       this.selectedType.emit(detail);
     }
   }  
 
   onBlur() {
     if (this.showErrors) {
       this.message = null;
       this.dropdownState = null;
       this.showErrorWarning();
       this.showInvalid();
     }
   }
 
   showInvalid() {
     const found = this.complexData.find((element) => element.displayText === this.dropdownForm.value);
     if (!found && this.dropdownForm.value) {
       this.message = 'Invalid input';
       this.dropdownState = 'warning';
       this.selectedType.emit(null);
     }
     if (!found && !this.dropdownForm.value) {
       this.message = this.labelName + ' is required.';
       this.dropdownState = this.isSubmitted ? 'error' : 'warning';
       this.selectedType.emit(null);
     }
   }
 
   ngOnDestroy(): void {
     this.destroy$.next();
     this.destroy$.complete();
   }
 
   findStatus(status) {
     const selectedGroups = [];
     status.forEach(code => {
       selectedGroups.push(this.propertyTypeValues.find(g => { return g.code === code; }));
     });
     return selectedGroups;
   }
 
   getClass() {
     if (this.customCss) {
       return this.customCss;
     }
     else if (this.isGlobalSearch) {
       return 'global-search-field-width';
     } else if (this.fullWidth) {
       return 'full-width';
     } else {
       return 'location-form-field';
     }
   }
 
 
 }
 